// Уход на оплату и на пробный в биллинг экосистемы (гайд §4.5, §4.6).
//
// ОДНА точка на приложение: экран подписки, апселл-модалка и старый адрес
// `/billing/checkout` зовут её же. Здесь и только здесь живут три вещи, которые
// иначе разъедутся по трём файлам:
//
//   1. РЕАЛЬНЫЙ вызов процедуры (`iecId.checkout.start` / `iecId.trial.start`),
//      а не имитация успеха — handoff одноразовый и живёт 60 секунд, поэтому
//      его нельзя ни закэшировать, ни положить в разметку заранее;
//   2. снимок уровня ПЕРЕД уходом — без него экран возврата не отличит «право
//      уже было» от «право только что приехало» (§4.5: токена заказа в
//      возврате нет и не будет);
//   3. правило перехода по платформам: web уходит текущей вкладкой (возврат
//      приведёт обратно), Capacitor — системным браузером, иначе приложение
//      уедет с бандла без пути назад.

import { toast } from "@studyhub/ui/toast";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { toastError } from "@/lib/user-facing-error";
import type { CheckoutBaseline, Tier } from "./_entitlement";

/** Ключ снимка. `sessionStorage`, а не `localStorage`: он нужен ровно на один заход. */
const BASELINE_KEY = "studyhub:iec-checkout-baseline";

/** Признак возврата из их витрины — его дописывает СЕРВЕР в `return`, не мы. */
export const IEC_RETURN_FLAG = "return";

export function rememberCheckoutBaseline(snapshot: { tier: Tier; serviceTier: Tier }): void {
	try {
		const payload: CheckoutBaseline = { ...snapshot, at: Date.now() };
		sessionStorage.setItem(BASELINE_KEY, JSON.stringify(payload));
	} catch {
		// Приватный режим / отключённое хранилище. Экран возврата переживёт:
		// без снимка он считает подтверждением непустое право от экосистемы.
	}
}

export function readCheckoutBaseline(): CheckoutBaseline | null {
	try {
		const raw = sessionStorage.getItem(BASELINE_KEY);
		if (!raw) return null;
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;
		const { tier, serviceTier, at } = parsed as Partial<CheckoutBaseline>;
		if (!isTier(tier) || !isTier(serviceTier) || typeof at !== "number") return null;
		return { tier, serviceTier, at };
	} catch {
		return null;
	}
}

export function forgetCheckoutBaseline(): void {
	try {
		sessionStorage.removeItem(BASELINE_KEY);
	} catch {
		// см. `rememberCheckoutBaseline`
	}
}

function isTier(value: unknown): value is Tier {
	return value === "free" || value === "plus" || value === "pro";
}

/**
 * Уводит браузер на выданный адрес.
 *
 * Web — текущей вкладкой: возврат по нашему адресу приведёт человека обратно, а
 * вторая вкладка оставила бы «мёртвый» экран подписки в первой. Capacitor —
 * системным браузером (`_blank`), потому что навигация WebView увела бы
 * приложение с бандла; обратно человека втянет universal link на
 * `study.iec.exchange`.
 */
function leaveToCheckout(url: string): void {
	if (IS_CAPACITOR) {
		window.open(url, "_blank", "");
		return;
	}
	window.location.assign(url);
}

export interface IecCheckoutApi {
	/** Купить уровень. `period` — `monthly` | `yearly`, как в их прайсе. */
	startCheckout: (args: { tier: "plus" | "pro"; period: "monthly" | "yearly" }) => void;
	/** Начать пробный. Срок берёт сервер из `trial.days` — мы его не зашиваем. */
	startTrial: () => void;
	/** Идёт запрос кода перехода: кнопке пора в `disabled`. */
	isPending: boolean;
}

/**
 * @param snapshot текущий уровень — он же снимок «до оплаты». `undefined`, пока
 *   права не загрузились: тогда снимок не пишется, и экран возврата откатится
 *   на слабое правило «право от экосистемы появилось».
 */
export function useIecCheckout(snapshot?: { tier: Tier; serviceTier: Tier }): IecCheckoutApi {
	const { t } = useTranslation("billing");
	const checkout = api.iecId.checkout.start.useMutation();
	const trial = api.iecId.trial.start.useMutation();

	const onError = useCallback(
		(err: unknown) => {
			toastError(err, t("iec.checkoutFailed"), t);
		},
		[t],
	);

	const startCheckout = useCallback(
		(args: { tier: "plus" | "pro"; period: "monthly" | "yearly" }) => {
			if (snapshot) rememberCheckoutBaseline(snapshot);
			checkout.mutate(args, {
				onSuccess: (res) => leaveToCheckout(res.url),
				onError,
			});
		},
		[checkout, onError, snapshot],
	);

	const startTrial = useCallback(() => {
		if (snapshot) rememberCheckoutBaseline(snapshot);
		trial.mutate(undefined, {
			onSuccess: (res) => leaveToCheckout(res.url),
			onError: (err) => {
				// `IEC_TRIAL_UNAVAILABLE` — не авария: политику могли сузить или
				// пробный уже израсходован. Говорим это прямо, а не «ошибка».
				if (err.message === "IEC_TRIAL_UNAVAILABLE") {
					toast.error(t("iec.trialUnavailable"));
					return;
				}
				onError(err);
			},
		});
	}, [onError, snapshot, t, trial]);

	return { startCheckout, startTrial, isPending: checkout.isPending || trial.isPending };
}
